/**
 * Hero globe (spec §2.4): a slowly rotating Earth with no basemap — painted
 * areas from the hourly texture, live activity as pulses at level-13 cells.
 * Supports the first-launch descent (spec §2.5): ease toward a target cell,
 * then hand off to the 2D map.
 */
import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { activityCellCenter, isTileToken, tileCenterLatLng, tileFromToken } from '@drawearth/geo';
import type { ActivityBatch, GlobalLivePainter } from '@drawearth/shared';
import { avatarDataUrl } from './Widgets.tsx';
import { WS_URL } from '../config.ts';
import { api } from '../lib/api.ts';
import { onThemeChange, themeColours } from '../lib/theme.ts';

export interface GlobeProps {
  /** Rotation speed in revolutions per minute (default 1). */
  rpm?: number;
  /** When set, ease the camera toward this point; `onArrive` fires at the end. */
  target?: { lat: number; lng: number } | null;
  onArrive?: () => void;
  onCellsViewed?: (cells: string[]) => void;
  /**
   * Fired when the viewer zooms past the surface, or taps a live callout, so
   * the page can hand off to the flat map at the same place (spec §2.5).
   */
  onEnter?: (p: { lat: number; lng: number; zoom: number }) => void;
  interactive?: boolean;
  className?: string;
}

/** Camera distance at which the sphere gives way to the flat map. */
const ENTER_DIST = 1.28;
/** Zoom the flat map opens at when you zoom in, still spherical so the move continues. */
const ENTER_ZOOM = 6;
/**
 * Tapping a painter opens the map on the artwork near them. A canvas tile is
 * about 306 m, and this zoom frames roughly one, so the drawing fills the view
 * rather than sitting somewhere in a kilometre-wide cell.
 */
const CALLOUT_ZOOM = 18;

function latLngToVec3(lat: number, lng: number, r: number): THREE.Vector3 {
  const phi = ((90 - lat) * Math.PI) / 180;
  const theta = ((lng + 180) * Math.PI) / 180;
  return new THREE.Vector3(-r * Math.sin(phi) * Math.cos(theta), r * Math.cos(phi), r * Math.sin(phi) * Math.sin(theta));
}

/** Line segments for parallels and meridians every `stepDeg` degrees. */
function graticuleGeometry(r: number, stepDeg: number): THREE.BufferGeometry {
  const pts: number[] = [];
  const push = (a: THREE.Vector3, b: THREE.Vector3) => pts.push(a.x, a.y, a.z, b.x, b.y, b.z);
  for (let lat = -90 + stepDeg; lat < 90; lat += stepDeg) {
    for (let lng = -180; lng < 180; lng += 3) push(latLngToVec3(lat, lng, r), latLngToVec3(lat, lng + 3, r));
  }
  for (let lng = -180; lng < 180; lng += stepDeg) {
    for (let lat = -90; lat < 90; lat += 3) push(latLngToVec3(lat, lng, r), latLngToVec3(lat + 3, lng, r));
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
  return g;
}

interface Pulse {
  mesh: THREE.Mesh;
  born: number;
  count: number;
}

export function Globe({ rpm = 1, target = null, onArrive, onCellsViewed, onEnter, interactive = true, className }: GlobeProps) {
  const ref = useRef<HTMLDivElement>(null);
  const targetRef = useRef(target);
  targetRef.current = target;
  const arriveRef = useRef(onArrive);
  arriveRef.current = onArrive;
  const viewedRef = useRef(onCellsViewed);
  viewedRef.current = onCellsViewed;
  const enterRef = useRef(onEnter);
  enterRef.current = onEnter;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    renderer.setSize(el.clientWidth, el.clientHeight);
    el.appendChild(renderer.domElement);
    // HTML overlay for live painter callouts (opt-in avatars, spec §2.4), projected from the sphere.
    const overlay = document.createElement('div');
    overlay.className = 'globe-callouts';
    el.appendChild(overlay);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, el.clientWidth / el.clientHeight, 0.1, 100);
    // The globe is the hero: framed large and a little high so the copy sits below it.
    // `dist` is the camera's distance from the centre; zooming moves it toward
    // the surface and the framing straightens up as it gets close.
    const framing = (): { home: number; y: number; look: number } => {
      const portrait = el.clientHeight > el.clientWidth;
      return portrait ? { home: 5.2, y: -0.75, look: -0.45 } : { home: 3.45, y: -0.25, look: -0.1 };
    };
    let dist = framing().home;
    const applyCamera = (): void => {
      const f = framing();
      const t = Math.max(0, Math.min(1, (dist - ENTER_DIST) / (f.home - ENTER_DIST)));
      camera.position.set(0, f.y * t, dist);
      camera.lookAt(0, f.look * t, 0);
      camera.updateProjectionMatrix();
    };
    applyCamera();

    const globe = new THREE.Group();
    scene.add(globe);
    const colours = themeColours();
    const baseMat = new THREE.MeshPhongMaterial({ color: colours.globe, emissive: colours.globeEmissive, shininess: 6, specular: colours.globeSpecular });
    const base = new THREE.Mesh(new THREE.SphereGeometry(1, 96, 96), baseMat);
    globe.add(base);
    // Faint graticule for orientation: parallels and meridians every 15°.
    const gridMat = new THREE.LineBasicMaterial({ color: colours.globeGrid, transparent: true, opacity: colours.gridOpacity });
    const grid = new THREE.LineSegments(graticuleGeometry(1.003, 15), gridMat);
    globe.add(grid);
    const equatorMat = new THREE.LineBasicMaterial({ color: colours.globeGrid, transparent: true, opacity: Math.min(1, colours.gridOpacity * 2) });
    const equator = new THREE.LineSegments(graticuleGeometry(1.004, 90), equatorMat);
    globe.add(equator);
    const paintedMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 1, depthWrite: false });
    const painted = new THREE.Mesh(new THREE.SphereGeometry(1.004, 96, 96), paintedMat);
    painted.visible = false;
    globe.add(painted);
    const atmoMat = new THREE.MeshBasicMaterial({ color: colours.atmosphere, transparent: true, opacity: 0.14, side: THREE.BackSide, blending: THREE.AdditiveBlending, depthWrite: false });
    const atmosphere = new THREE.Mesh(new THREE.SphereGeometry(1.08, 64, 64), atmoMat);
    scene.add(atmosphere);
    const rimMat = new THREE.MeshBasicMaterial({ color: colours.atmosphere, transparent: true, opacity: 0.05, side: THREE.BackSide, blending: THREE.AdditiveBlending, depthWrite: false });
    const rim = new THREE.Mesh(new THREE.SphereGeometry(1.03, 64, 64), rimMat);
    scene.add(rim);
    const applyTheme = (): void => {
      const c = themeColours();
      baseMat.color.setHex(c.globe);
      baseMat.emissive.setHex(c.globeEmissive);
      baseMat.specular.setHex(c.globeSpecular);
      gridMat.color.setHex(c.globeGrid);
      gridMat.opacity = c.gridOpacity;
      equatorMat.color.setHex(c.globeGrid);
      equatorMat.opacity = Math.min(1, c.gridOpacity * 2);
      atmoMat.color.setHex(c.atmosphere);
      rimMat.color.setHex(c.atmosphere);
      // Additive glow only works over dark space; on white, blend normally.
      const light = c.ground === '#ffffff';
      atmoMat.blending = light ? THREE.NormalBlending : THREE.AdditiveBlending;
      atmoMat.opacity = light ? 0.12 : 0.14;
      rimMat.visible = !light;
      atmoMat.needsUpdate = true;
    };
    applyTheme();
    const unsubTheme = onThemeChange(applyTheme);
    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const sun = new THREE.DirectionalLight(0xfff1d6, 1.2);
    sun.position.set(5, 3, 4);
    scene.add(sun);

    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    const dbg: Record<string, unknown> | null = import.meta.env.DEV ? ((window as unknown as { __globe?: Record<string, unknown> }).__globe ??= {}) : null;
    loader.load(
      `${api.globeTextureUrl()}?t=${Math.floor(Date.now() / 60_000)}`,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.magFilter = THREE.LinearFilter;
        tex.minFilter = THREE.LinearFilter;
        tex.generateMipmaps = false;
        paintedMat.map = tex;
        paintedMat.needsUpdate = true;
        painted.visible = true;
        if (dbg) Object.assign(dbg, { textureLoaded: true, size: [tex.image?.width, tex.image?.height] });
      },
      undefined,
      (err) => {
        // No texture yet (fresh deployment) — the globe stays dark.
        if (dbg) Object.assign(dbg, { textureError: String(err) });
      },
    );

    // --- activity pulses ---
    const pulses: Pulse[] = [];
    const pulseGeom = new THREE.RingGeometry(0.01, 0.02, 32);
    const addPulse = (cell: string, count: number): void => {
      const { lat, lng } = activityCellCenter(cell);
      const pos = latLngToVec3(lat, lng, 1.01);
      const mat = new THREE.MeshBasicMaterial({ color: themeColours().pulse, transparent: true, opacity: 0.95, side: THREE.DoubleSide, depthWrite: false });
      const mesh = new THREE.Mesh(pulseGeom, mat);
      mesh.position.copy(pos);
      mesh.lookAt(pos.clone().multiplyScalar(2));
      globe.add(mesh);
      const dot = new THREE.Mesh(new THREE.CircleGeometry(0.006, 16), new THREE.MeshBasicMaterial({ color: 0xfff3c4, side: THREE.DoubleSide, depthWrite: false }));
      dot.position.copy(pos);
      dot.lookAt(pos.clone().multiplyScalar(2));
      mesh.add(dot);
      dot.position.set(0, 0, 0);
      pulses.push({ mesh, born: performance.now(), count });
    };
    const applyBatch = (b: ActivityBatch): void => {
      for (const c of b.cells) addPulse(c.cell, c.count);
      viewedRef.current?.(b.cells.map((c) => c.cell));
    };
    void api.activity().then(applyBatch).catch(() => undefined);
    let ws: WebSocket | null = null;
    try {
      ws = new WebSocket(`${WS_URL}/v1/activity/ws`);
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(String(ev.data)) as ActivityBatch | { type: string };
          if (msg.type === 'activity') applyBatch(msg as ActivityBatch);
        } catch {
          // ignore
        }
      };
    } catch {
      ws = null;
    }

    /** Callouts the pointer is currently over; while any, the globe holds still. */
    let hovering = 0;

    // --- live painter callouts ---
    interface Callout { el: HTMLElement; pos: THREE.Vector3; lat: number; lng: number; cell: string; ts: number }
    const callouts = new Map<number, Callout>();
    const applyLive = (painters: GlobalLivePainter[]): void => {
      const seen = new Set<number>();
      for (const p of painters.slice(0, 40)) {
        seen.add(p.userId);
        const { lat, lng } = activityCellCenter(p.cell);
        const pos = latLngToVec3(lat, lng, 1.01);
        let c = callouts.get(p.userId);
        if (!c) {
          const node = document.createElement('button');
          node.type = 'button';
          node.className = 'callout callout--globe callout--link';
          node.dataset.userId = String(p.userId);
          const av = document.createElement('div');
          av.className = 'callout__avatar';
          node.append(av);
          overlay.appendChild(node);
          c = { el: node, pos, lat, lng, cell: p.cell, ts: p.ts };
          callouts.set(p.userId, c);
          // Tapping a live painter drops down to where they are painting.
          // The globe holds still while the pointer is on a callout, so the
          // target does not drift out from under the cursor.
          const entry = c;
          node.addEventListener('click', () => void flyToPainter(entry));
          node.addEventListener('pointerenter', () => (hovering += 1));
          node.addEventListener('pointerleave', () => (hovering = Math.max(0, hovering - 1)));
          requestAnimationFrame(() => node.classList.add('is-in'));
        }
        c.pos = pos;
        c.lat = lat;
        c.lng = lng;
        c.cell = p.cell;
        c.ts = p.ts;
        const av = c.el.querySelector('.callout__avatar') as HTMLElement;
        const url = avatarDataUrl(p.avatar);
        av.style.backgroundImage = url ? `url(${url})` : '';
        av.classList.toggle('callout__avatar--empty', !url);
        c.el.title = p.handle;
        c.el.setAttribute('aria-label', `Go to where ${p.handle} is painting`);
      }
      for (const [uid, c] of callouts) {
        if (!seen.has(uid)) {
          c.el.remove();
          callouts.delete(uid);
        }
      }
    };
    const loadLive = (): void => {
      void api
        .live()
        .then((r) => applyLive(r.painters))
        .catch(() => undefined);
    };
    loadLive();
    const liveTimer = setInterval(loadLive, 8000);
    const camDir = new THREE.Vector3();
    const world = new THREE.Vector3();
    const normal = new THREE.Vector3();
    const placeCallouts = (): void => {
      if (callouts.size === 0) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      camera.getWorldDirection(camDir);
      for (const c of callouts.values()) {
        world.copy(c.pos);
        globe.localToWorld(world);
        normal.copy(world).normalize();
        // Facing the camera? (camera looks along camDir; a front-facing point's normal opposes it)
        const facing = normal.dot(camDir) < -0.12;
        world.project(camera);
        const x = (world.x + 1) / 2 * w;
        const y = (1 - world.y) / 2 * h;
        // Keep clear of the edges and the top bar so pills never sit under controls.
        const onScreen = facing && x > 70 && x < w - 70 && y > 110 && y < h - 20;
        c.el.classList.toggle('is-hidden', !onScreen);
        if (onScreen) c.el.style.transform = `translate(${x}px, ${y - 12}px) translate(-50%, -100%)`;
      }
    };

    // --- camera flights (first-launch descent, callout taps) ---
    interface Flight {
      start: number;
      dur: number;
      fromRotY: number;
      fromTilt: number;
      fromDist: number;
      toRotY: number;
      toTilt: number;
      toDist: number;
      /** A hand on the globe cancels a flight the viewer started, never the intro. */
      cancelable: boolean;
      done: () => void;
    }
    let flight: Flight | null = null;

    // --- interaction ---
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    let velocity = 0;
    let tilt = 0.25;
    /** Set once the viewer spins the globe by hand; idle rotation stops for good. */
    let spunByHand = false;
    /** Live pointers, so two fingers can pinch to zoom. */
    const touches = new Map<number, { x: number; y: number }>();
    let pinchFrom = 0;

    /** The point on the sphere facing the camera — the inverse of the descent maths. */
    const facingLatLng = (): { lat: number; lng: number } => {
      const lat = Math.max(-85, Math.min(85, (tilt * 180) / Math.PI));
      const raw = (-globe.rotation.y * 180) / Math.PI - 90;
      return { lat, lng: ((((raw + 180) % 360) + 360) % 360) - 180 };
    };
    let entered = false;
    const enter = (at?: { lat: number; lng: number }, zoom = ENTER_ZOOM): void => {
      if (entered || !enterRef.current) return;
      entered = true;
      enterRef.current({ ...(at ?? facingLatLng()), zoom });
    };
    let zoomedByHand = false;
    const zoomBy = (factor: number): void => {
      const f = framing();
      zoomedByHand = true;
      dist = Math.max(ENTER_DIST, Math.min(f.home * 1.4, dist * factor));
      applyCamera();
      if (dist <= ENTER_DIST + 0.001) enter();
    };
    /**
     * Tapping a painter should land on the drawing, not on the middle of the
     * kilometre-wide cell their callout sits on. The live list deliberately
     * says no more than the cell, so the artwork is found from the public
     * per-tile aggregate instead, which names no one.
     */
    const flyToPainter = async (c: Callout): Promise<void> => {
      let land: { lat: number; lng: number } | undefined;
      try {
        const r = await api.cellTiles(c.cell);
        const best = r.tiles.find((t) => isTileToken(t.tile));
        if (best) land = tileCenterLatLng(tileFromToken(best.tile));
      } catch {
        // Fall back to the cell centre.
      }
      flyTo(c.lat, c.lng, land);
    };

    /**
     * Swing the given place to face the camera, drop toward it, then hand off.
     * `land` is where the map opens.
     */
    const flyTo = (lat: number, lng: number, land?: { lat: number; lng: number }): void => {
      if (entered) return;
      spunByHand = true;
      flight = {
        start: performance.now(),
        dur: 1500,
        fromRotY: globe.rotation.y,
        fromTilt: tilt,
        fromDist: dist,
        toRotY: -((lng + 90) * Math.PI) / 180,
        toTilt: (lat * Math.PI) / 180,
        toDist: ENTER_DIST,
        cancelable: true,
        done: () => enter(land ?? { lat, lng }, CALLOUT_ZOOM),
      };
    };

    const onDown = (e: PointerEvent): void => {
      if (!interactive) return;
      touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (touches.size === 2) {
        const [a, b] = [...touches.values()];
        pinchFrom = Math.hypot(a.x - b.x, a.y - b.y);
        dragging = false;
        return;
      }
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
    };
    const onMove = (e: PointerEvent): void => {
      if (touches.has(e.pointerId)) touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (touches.size === 2) {
        const [a, b] = [...touches.values()];
        const now = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinchFrom > 0 && now > 0) zoomBy(pinchFrom / now);
        pinchFrom = now;
        return;
      }
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      // A real spin, not the jitter of a tap: take over from the idle rotation.
      if (Math.abs(dx) + Math.abs(dy) > 1.5) {
        spunByHand = true;
        if (flight?.cancelable) flight = null;
      }
      globe.rotation.y += dx * 0.005;
      tilt = Math.max(-1.2, Math.min(1.2, tilt + dy * 0.005));
      velocity = dx * 0.005;
    };
    const onUp = (e: PointerEvent): void => {
      touches.delete(e.pointerId);
      if (touches.size < 2) pinchFrom = 0;
      dragging = false;
    };
    const onWheel = (e: WheelEvent): void => {
      if (!interactive) return;
      e.preventDefault();
      spunByHand = true;
      zoomBy(Math.exp(e.deltaY * 0.0016));
    };
    renderer.domElement.addEventListener('pointerdown', onDown);
    renderer.domElement.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    if (dbg) Object.assign(dbg, { state: () => ({ rotY: globe.rotation.y, tilt, dist, spunByHand, callouts: callouts.size }) });

    // --- animation ---
    let raf = 0;
    let last = performance.now();
    let descending = false;
    const animate = (now: number): void => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const t = targetRef.current;
      if (t && !flight && !descending) {
        // Rotate so the target faces the camera: camera looks down -z at the globe's +z side.
        descending = true;
        flight = {
          start: now,
          dur: 2600,
          fromRotY: globe.rotation.y,
          fromTilt: tilt,
          fromDist: dist,
          toRotY: -((t.lng + 90) * Math.PI) / 180,
          toTilt: (t.lat * Math.PI) / 180,
          toDist: 1.35,
          cancelable: false,
          done: () => arriveRef.current?.(),
        };
      }
      if (flight) {
        const p = Math.min(1, (now - flight.start) / flight.dur);
        const ease = 1 - Math.pow(1 - p, 3);
        // Shortest rotation path.
        let dRot = flight.toRotY - flight.fromRotY;
        dRot = Math.atan2(Math.sin(dRot), Math.cos(dRot));
        globe.rotation.y = flight.fromRotY + dRot * ease;
        tilt = flight.fromTilt + (flight.toTilt - flight.fromTilt) * ease;
        dist = flight.fromDist + (flight.toDist - flight.fromDist) * ease;
        applyCamera();
        if (p >= 1) {
          const done = flight.done;
          flight = null;
          done();
        }
      } else if (!dragging) {
        // Inertia is measured per 60fps frame, so scale it by the real elapsed
        // time — otherwise a slow frame rate spins the globe further.
        const frames = dt * 60;
        // Idle rotation runs until the viewer takes the globe in hand; after
        // that it only coasts to a stop and stays where they left it.
        if (!spunByHand && hovering === 0) globe.rotation.y += ((rpm * 2 * Math.PI) / 60) * dt;
        globe.rotation.y += velocity * frames;
        velocity *= Math.exp(-3.7 * dt);
      }
      globe.rotation.x = tilt;
      const nowMs = performance.now();
      for (let i = pulses.length - 1; i >= 0; i--) {
        const pl = pulses[i];
        const age = (nowMs - pl.born) / 1000;
        const life = 6 + Math.min(10, pl.count);
        if (age > life) {
          globe.remove(pl.mesh);
          (pl.mesh.material as THREE.Material).dispose();
          pulses.splice(i, 1);
          continue;
        }
        const phase = (age % 2) / 2;
        pl.mesh.scale.setScalar(1 + phase * 3);
        (pl.mesh.material as THREE.MeshBasicMaterial).opacity = (1 - phase * 0.8) * Math.sqrt(1 - age / life);
      }
      renderer.render(scene, camera);
      placeCallouts();
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    const onResize = (): void => {
      renderer.setSize(el.clientWidth, el.clientHeight);
      camera.aspect = el.clientWidth / el.clientHeight;
      // Refit to the new portrait/landscape framing, unless the viewer set the zoom.
      if (zoomedByHand || flight) dist = Math.max(ENTER_DIST, Math.min(framing().home * 1.4, dist));
      else dist = framing().home;
      applyCamera();
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('pointerdown', onDown);
      renderer.domElement.removeEventListener('wheel', onWheel);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      ws?.close();
      unsubTheme();
      clearInterval(liveTimer);
      overlay.remove();
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpm, interactive]);

  return <div ref={ref} className={`globe ${className ?? ''}`} data-testid="globe" />;
}
